import { Quote, AppSettings, ApiResponse } from '../types';
import { INITIAL_QUOTES } from './defaultQuotes';

const LOCAL_STORAGE_KEY = 'day_quotes_d1_fallback_v1';
const ADMIN_TOKEN_KEY = 'day_quotes_admin_session_token';
const SETTINGS_KEY = 'day_quotes_app_settings';

const DEFAULT_SETTINGS: AppSettings = {
  since_date: '2026-09-22'
};

export function getLocalSettings(): AppSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
    }
  } catch (e) {
    console.error('Failed to parse local settings', e);
  }
  return DEFAULT_SETTINGS;
}

export function saveLocalSettings(settings: AppSettings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save settings', e);
  }
}

export function getLocalQuotes(includeAll: boolean = true): Quote[] {
  let list: Quote[] = [];
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        list = parsed;
      }
    }
  } catch (e) {
    console.error('Failed to parse local quotes', e);
  }

  if (list.length === 0) {
    list = INITIAL_QUOTES;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(INITIAL_QUOTES));
  }

  if (!includeAll) {
    return list.filter((q) => q.is_active !== 0);
  }
  return list;
}

function saveLocalQuotes(quotes: Quote[]) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(quotes));
  } catch (e) {
    console.error('Failed to save to localStorage', e);
  }
}

// Session-based admin token management
export function getAdminToken(): string | null {
  return sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token: string) {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken() {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

// Authentication against server
export async function loginAdmin(password: string): Promise<boolean> {
  try {
    const res = await fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password })
    });
    if (res.ok) {
      const data = await res.json() as { success: boolean; token?: string };
      if (data.success && data.token) {
        setAdminToken(data.token);
        return true;
      }
    }
  } catch (err) {
    console.warn('API /api/auth unreachable, falling back to local password check', err);
  }

  // Fallback for standalone offline dev
  if (password === 'admin123') {
    const mockToken = btoa(`admin:admin123:${Date.now()}`);
    setAdminToken(mockToken);
    return true;
  }
  return false;
}

export async function fetchSettings(): Promise<{ settings: AppSettings; isD1: boolean }> {
  try {
    const res = await fetch('/api/settings');
    if (res.ok) {
      const json = await res.json() as ApiResponse<Record<string, string>>;
      if (json.success && json.data) {
        const result: AppSettings = {
          since_date: json.data.since_date || DEFAULT_SETTINGS.since_date
        };
        saveLocalSettings(result);
        return { settings: result, isD1: json.source === 'd1' };
      }
    }
  } catch (e) {
    console.warn('Settings API not reachable, using local settings', e);
  }
  return { settings: getLocalSettings(), isD1: false };
}

export async function updateSetting(key: string, value: string): Promise<boolean> {
  const token = getAdminToken();
  if (!token) throw new Error('Admin authentication required');

  try {
    const res = await fetch('/api/settings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ key, value })
    });
    if (res.ok) {
      const current = getLocalSettings();
      saveLocalSettings({ ...current, [key]: value });
      return true;
    }
  } catch (e) {
    console.warn('Failed to save setting to API, updating locally', e);
  }

  const current = getLocalSettings();
  saveLocalSettings({ ...current, [key]: value });
  return true;
}

export async function fetchQuotes(dateFilter?: string, includeAll: boolean = false): Promise<{ quotes: Quote[]; isD1: boolean }> {
  try {
    const params = new URLSearchParams();
    if (dateFilter) params.append('date', dateFilter);
    if (includeAll) params.append('all', 'true');

    const url = `/api/quotes${params.toString() ? '?' + params.toString() : ''}`;
    const headers: Record<string, string> = {};
    const token = getAdminToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(url, { headers });
    if (res.ok) {
      const json = await res.json() as ApiResponse<Quote[]>;
      if (json.success && Array.isArray(json.data)) {
        return { quotes: json.data, isD1: json.source === 'd1' };
      }
    }
  } catch (e) {
    console.warn('Quotes API unreachable, using local quotes', e);
  }

  let list = getLocalQuotes(includeAll);
  if (dateFilter) {
    list = [...list].sort((a, b) => {
      if (a.assigned_date === dateFilter) return -1;
      if (b.assigned_date === dateFilter) return 1;
      return 0;
    });
  }

  return { quotes: list, isD1: false };
}

export async function addQuote(newQuote: {
  text: string;
  author?: string;
  category?: string;
  assigned_date?: string | null;
  is_active?: number;
}): Promise<Quote> {
  const token = getAdminToken();
  if (!token) throw new Error('Admin authentication required');

  try {
    const res = await fetch('/api/quotes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(newQuote)
    });

    if (res.ok) {
      const json = await res.json() as ApiResponse<Quote>;
      if (json.success && json.data) {
        // Also update local list cache
        const list = [json.data, ...getLocalQuotes(true)];
        saveLocalQuotes(list);
        return json.data;
      }
    }
  } catch (e) {
    console.warn('Using local fallback for addQuote', e);
  }

  const created: Quote = {
    id: 'local_' + Date.now(),
    text: newQuote.text.trim(),
    author: newQuote.author?.trim() || 'Anonymous',
    category: newQuote.category?.trim() || 'General',
    assigned_date: newQuote.assigned_date && newQuote.assigned_date.trim() ? newQuote.assigned_date.trim() : null,
    created_at: Date.now(),
    is_active: newQuote.is_active !== undefined ? newQuote.is_active : 1
  };
  const list = [created, ...getLocalQuotes(true)];
  saveLocalQuotes(list);
  return created;
}

export async function updateQuote(id: string, updates: Partial<Quote>): Promise<Quote | null> {
  const token = getAdminToken();
  if (!token) throw new Error('Admin authentication required');

  try {
    const res = await fetch(`/api/quotes/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });

    if (res.ok) {
      const json = await res.json() as ApiResponse<Quote>;
      if (json.success && json.data) {
        const list = getLocalQuotes(true).map((q) => (q.id === id ? { ...q, ...json.data } : q));
        saveLocalQuotes(list);
        return json.data;
      }
    }
  } catch (e) {
    console.warn('Using local fallback for updateQuote', e);
  }

  const list = getLocalQuotes(true).map((q) => (q.id === id ? { ...q, ...updates } : q));
  saveLocalQuotes(list);
  return list.find((q) => q.id === id) || null;
}

export async function toggleQuoteActive(id: string, newActiveState: boolean): Promise<Quote | null> {
  return updateQuote(id, { is_active: newActiveState ? 1 : 0 });
}

export async function assignQuoteDate(id: string, assignedDate: string | null): Promise<Quote | null> {
  // When assigning a quote to a date, ensure it is active as well
  return updateQuote(id, { 
    assigned_date: assignedDate && assignedDate.trim() ? assignedDate.trim() : null,
    is_active: 1 
  });
}

export async function deleteQuote(id: string): Promise<boolean> {
  const token = getAdminToken();
  if (!token) throw new Error('Admin authentication required');

  try {
    const res = await fetch(`/api/quotes/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (res.ok) {
      const list = getLocalQuotes(true).filter((q) => q.id !== id);
      saveLocalQuotes(list);
      return true;
    }
  } catch (e) {
    console.warn('Using local fallback for deleteQuote', e);
  }

  const list = getLocalQuotes(true).filter((q) => q.id !== id);
  saveLocalQuotes(list);
  return true;
}

export async function resetToDefaultQuotes(): Promise<Quote[]> {
  const token = getAdminToken();
  if (!token) throw new Error('Admin authentication required');

  try {
    await fetch('/api/quotes/seed', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  } catch (e) {
    console.warn('Could not call /api/quotes/seed on server', e);
  }

  saveLocalQuotes(INITIAL_QUOTES);
  return INITIAL_QUOTES;
}
